import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from './cn';

const variants = {
  primary:
    'bg-stitch-primary text-white hover:bg-stitch-primary-hover shadow-stitch-card dark:shadow-none active:scale-[0.98]',
  secondary:
    'bg-stitch-surface-2 text-stitch-fg border border-stitch-border hover:border-stitch-primary/25 hover:bg-stitch-primary-soft/40 dark:hover:bg-stitch-surface-2/90',
  outline:
    'border-2 border-stitch-primary bg-transparent text-stitch-primary hover:bg-stitch-primary-soft dark:hover:bg-stitch-primary-soft/20',
  ghost:
    'text-stitch-fg hover:bg-stitch-surface-2 dark:hover:bg-stitch-surface-2/80',
  danger: 'bg-stitch-danger text-white hover:opacity-90 active:scale-[0.98]',
  accent:
    'bg-stitch-accent text-white hover:bg-stitch-accent-hover shadow-stitch-card dark:shadow-none active:scale-[0.98]',
} as const;

const sizes = {
  sm: 'px-3 py-1.5 text-sm rounded-stitch-md',
  md: 'px-4 py-2 text-sm rounded-stitch-lg',
  lg: 'px-6 py-3 text-base rounded-stitch-xl font-semibold',
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center font-medium transition-[color,background-color,border-color,transform,box-shadow] duration-stitch ease-stitch-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stitch-ring focus-visible:ring-offset-2 focus-visible:ring-offset-stitch-canvas',
        'disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    />
  );
});
